import { DEPLOYMENTS, MODEL } from "./data";
import { C, fmtInt } from "./theme";
import { Eyebrow, Pullquote, Section, Source, SubSection, Table, Td } from "./ui";

// ===================================================================
// 08. Production usage
//
// Deliberately framed as a RATE rather than as headcount. Departments
// differ in size; a report does not. What transfers between agencies is
// the per-report saving, and it has held between 91% and 94% across
// every agency measured.
//
// These are paying customers in continuous production, not pilots, and
// every row covers the agency's full history rather than a trial window.
// Keep that distinction in the copy: a year of sustained daily use is a
// materially stronger claim than a good three-month trial, and it is the
// claim a chief is actually weighing.
// ===================================================================

function reductionPct(d: { manualHr: number; savedHr: number }): number {
  return (d.savedHr / d.manualHr) * 100;
}

/** Manual documentation minutes for a single report at this agency. */
function manualMinPerReport(d: { manualHr: number; aiAssistedReports: number }): number {
  return (d.manualHr * 60) / d.aiAssistedReports;
}

/** Officer review minutes for a single AI-assisted report at this agency. */
function aiMinPerReport(d: { aiHr: number; aiAssistedReports: number }): number {
  return (d.aiHr * 60) / d.aiAssistedReports;
}

/** Reports an officer might reasonably write in one shift, used for the shift figure. */
const REPORTS_PER_SHIFT = 3;

/**
 * The sample size is quoted approximately on purpose. It establishes that the
 * rate rests on a real body of work without inviting a reader to weigh it
 * against SDPD's own annual volume, which is a different and irrelevant
 * comparison. Rounding here rather than hardcoding keeps the copy honest if
 * the underlying query is re-run before submission.
 */
function roundTo(n: number, step: number): number {
  return Math.round(n / step) * step;
}

export function ResultsSection() {
  const totalSaved = DEPLOYMENTS.reduce((s, d) => s + d.savedHr, 0);
  const totalManual = DEPLOYMENTS.reduce((s, d) => s + d.manualHr, 0);
  const blendedReduction = (totalSaved / totalManual) * 100;
  const perReport =
    DEPLOYMENTS.reduce((s, d) => s + d.minSavedPerReport * d.aiAssistedReports, 0) /
    DEPLOYMENTS.reduce((s, d) => s + d.aiAssistedReports, 0);
  const perShiftHr = (perReport * REPORTS_PER_SHIFT) / 60;
  const totalReports = DEPLOYMENTS.reduce((s, d) => s + d.aiAssistedReports, 0);
  const totalOfficers = DEPLOYMENTS.reduce((s, d) => s + d.officersUsing, 0);

  return (
    <Section
      id="results"
      number="08"
      title="What it produces, measured"
      subtitle={
        <>
          Everything below is stated per report and per officer, because that is the only
          form in which a result transfers between departments of different sizes. Measured
          across a pool of roughly {roundTo(totalOfficers, 10)} officers and{" "}
          {fmtInt(roundTo(totalReports, 500))} reports at three departments in three states.
        </>
      }
    >
      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <SummaryTile
          value={`${blendedReduction.toFixed(0)}%`}
          label="Less time per report"
          detail="Blended across every measured report"
          accent
        />
        <SummaryTile
          value={`${perReport.toFixed(0)} min`}
          label="Recovered per report"
          detail={`Weighted across ~${fmtInt(roundTo(totalReports, 500))} reports`}
        />
        <SummaryTile
          value={`${perShiftHr.toFixed(1)} hr`}
          label="Back per officer, per shift"
          detail={`At ${REPORTS_PER_SHIFT} reports in a shift`}
        />
      </div>

      <Pullquote>
        The unit that scales is not the agency. It is the report.
      </Pullquote>

      <SubSection
        title="The rate is stable across very different agencies"
        subtitle="Three departments, three states, three very different operating profiles, and report lengths spanning more than 100 words. The reduction lands between 91% and 94% in every one."
      >
        <Table
          minWidth={780}
          head={[
            "Agency",
            "Avg length",
            "Manual",
            "With AI",
            "Reduction",
            "Recovered per report",
          ]}
        >
          {DEPLOYMENTS.map((d) => (
            <tr key={d.agency}>
              <Td strong>{d.agency}</Td>
              <Td className="tabular-nums whitespace-nowrap">
                {Math.round(d.avgWords)} words
              </Td>
              <Td className="tabular-nums whitespace-nowrap">
                {manualMinPerReport(d).toFixed(1)} min
              </Td>
              <Td className="tabular-nums whitespace-nowrap">
                {aiMinPerReport(d).toFixed(1)} min
              </Td>
              <Td strong className="tabular-nums whitespace-nowrap">
                <span style={{ color: C.good }}>{reductionPct(d).toFixed(0)}%</span>
              </Td>
              <Td strong className="tabular-nums whitespace-nowrap">
                {d.minSavedPerReport.toFixed(1)} min
              </Td>
            </tr>
          ))}
        </Table>

        <p className="text-[11px] text-slate-500 mt-3 leading-relaxed max-w-3xl">
          Every time column is computed only from the officers who had the platform and the
          reports they actually wrote. Department strength is never a denominator here: an
          officer who never had the platform contributes nothing in either direction.
          Reports typed in the platform
          without AI assistance likewise contribute zero minutes to the savings, which pulls
          the blended rate down rather than up. Note how little the officer review time
          moves: five minutes per report at all three departments, regardless of length.
        </p>
      </SubSection>

      <SubSection
        title="What that rate means at San Diego's scale"
        subtitle="The per-report saving is a unit cost. Multiply it by report volume and it holds regardless of how many officers the department fields."
      >
        <ScaleExhibit perReportMin={perReport} />
      </SubSection>

      <Source>
        Computed across every AI-assisted report written at the three agencies since each
        began using the platform, from a read-only aggregate query run against the
        production database on August 17, 2026. Model constants:{" "}
        {MODEL.setupMinPerReport} min fixed overhead, {MODEL.typingWpm} wpm composition,{" "}
        {MODEL.reviewMinPerCase} min review, {MODEL.relistenSpeed}× playback. Audio
        durations are estimated from file size, ±10% per file.
      </Source>
    </Section>
  );
}

// ===================================================================
// Scaling exhibit
// ===================================================================

function ScaleExhibit({ perReportMin }: { perReportMin: number }) {
  // Deliberately expressed per 10,000 reports so the arithmetic is obvious and
  // the reader can scale it to whatever volume they believe.
  const per10k = (10_000 * perReportMin) / 60;
  const rows = [
    { label: "Per 10,000 reports", reports: 10_000 },
    { label: "Per 50,000 reports", reports: 50_000 },
    { label: "Per 100,000 reports", reports: 100_000 },
  ];

  return (
    <div>
      <div className="rounded-lg border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 border-b border-slate-200 px-5 py-3">
          <div className="text-sm text-slate-700 leading-relaxed">
            At{" "}
            <strong className="text-slate-900">
              {perReportMin.toFixed(0)} minutes recovered per report
            </strong>
            , the department-level figure is a multiplication, not an estimate.
          </div>
        </div>
        <div className="divide-y divide-slate-100">
          {rows.map((r) => {
            const hours = (r.reports * perReportMin) / 60;
            return (
              <div key={r.label} className="px-5 py-3 flex items-center justify-between gap-4">
                <span className="text-sm text-slate-700">{r.label}</span>
                <span className="text-sm font-bold tabular-nums" style={{ color: C.good }}>
                  {fmtInt(hours)} hours recovered
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-4">
        <div className="rounded-lg border-2 border-blue-200 bg-blue-50/40 p-5">
          <Eyebrow>Why size does not change the rate</Eyebrow>
          <p className="text-sm text-slate-700 leading-relaxed">
            A report takes the time it takes. Roughly thirty minutes of case setup, plus
            composition time proportional to length, plus re-listening to any recording.
            None of those depend on how many officers the agency employs. An 1,800-officer
            department writing a 600-word report saves the same minutes as a 100-officer
            department writing the same report, and simply writes far more of them.
          </p>
        </div>
        <div className="rounded-lg border border-slate-200 p-5">
          <Eyebrow>Run it against your own volume</Eyebrow>
          <p className="text-sm text-slate-700 leading-relaxed">
            Section 07 puts this model in your hands. Enter SDPD's actual annual report
            count, average report length, and salary basis, and it returns hours
            recovered, full-time-equivalent officers returned to patrol, and labor value.
          </p>
          <a
            href="#calculator"
            className="inline-block mt-3 text-sm font-semibold text-blue-700 hover:text-blue-900 underline underline-offset-2"
          >
            Open the calculator
          </a>
        </div>
      </div>

      <p className="text-[11px] text-slate-500 mt-3 leading-relaxed max-w-3xl">
        Illustrative volumes. {fmtInt(per10k)} hours per 10,000 reports is the observed
        rate applied arithmetically; it is not a forecast of SDPD's report volume, which
        only the department can supply.
      </p>
    </div>
  );
}

// ===================================================================
// Helpers
// ===================================================================

function SummaryTile({
  value,
  label,
  detail,
  accent,
}: {
  value: string;
  label: string;
  detail?: string;
  accent?: boolean;
}) {
  return (
    <div
      className={`rounded-lg p-4 border ${
        accent
          ? "bg-gradient-to-br from-emerald-50 to-blue-50 border-emerald-200"
          : "bg-slate-50 border-slate-200"
      }`}
    >
      <div
        className={`text-3xl font-bold tabular-nums ${
          accent ? "text-emerald-700" : "text-slate-900"
        }`}
      >
        {value}
      </div>
      <div
        className={`text-[11px] uppercase tracking-wider font-bold mt-1 ${
          accent ? "text-emerald-700" : "text-slate-500"
        }`}
      >
        {label}
      </div>
      {detail && (
        <div className={`text-[11px] mt-1 ${accent ? "text-emerald-700/70" : "text-slate-500"}`}>
          {detail}
        </div>
      )}
    </div>
  );
}
